import { PusatResolusiV2RepositoryInterface } from "@/data/persistences/repositories/contracts/PusatResolusiV2RepositoryInterface";
import ApiService from "../../services/ApiService";
import { PusatResolusiV2Mapper } from "@/data/persistences/mappers/PusatResolusiV2Mapper";
import { PusatResolusiV2Endpoint } from "../../endpoints/PusatResolusiV2Endpoint";
import { CheckTicketPusatResolusiRequest, FinishIssuePusatResolusiV2Request, GetConversationRequest, ListTicketPusatResolusiRequest, SubmitNotePusatResolusiRequest, SendChatPusatResolusiRequest, ListSTTPusatResolusi, TicketCounterPusatResolusiRequest } from "@/data/payload/api/PusatResolusiV2ApiRequest";
import {
  CategoryPusatResolusi,
  CheckIssuePusatResolusi,
  TicketPusatResolusiDetail,
  TicketDetail,
  TicketAction,
  PusatResolusiSTTData,
  MentionsData,
  HistoryChatResponse,
  Conversation,
} from "@/domain/entities/PusatResolusiV2";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import { CreatePusatResolusiV2RequestInterface, SendChatPusatResolusiV2RequestInterface } from "@/data/payload/contracts/PusatResolusiV2Request";
import { getToken } from '../../misc/Cookies';
import { Endpoints } from '../../misc/Endpoints';

export class PusatResolusiV2ApiRepository
  implements PusatResolusiV2RepositoryInterface {
  private readonly service: ApiService;
  private readonly mapper: PusatResolusiV2Mapper;
  private readonly endpoints: PusatResolusiV2Endpoint;

  constructor(
    service: ApiService,
    mapper: PusatResolusiV2Mapper,
    endpoints: PusatResolusiV2Endpoint
  ) {
    this.service = service;
    this.mapper = mapper;
    this.endpoints = endpoints;
  }

  async checkTicket(
    params: CheckTicketPusatResolusiRequest
  ): Promise<TicketPusatResolusiDetail> {
    const res = await this.service.invoke(
      "GET",
      this.endpoints.checkTicket(params)
    );

    return this.mapper.convertCheckTicketPusatResolusiFromApi(res);
  }

  async getListCategory(
    params: CheckTicketPusatResolusiRequest
  ): Promise<CategoryPusatResolusi[]> {
    const res = await this.service.invoke(
      "GET",
      this.endpoints.getListCategory(params)
    );

    return this.mapper.convertListCategoryPusatResolusiFromApi(res);
  }

  async getTicketCounter(
    params: TicketCounterPusatResolusiRequest
  ) {
    const res = await this.service.invoke(
      "GET",
      this.endpoints.getTicketCounter(params)
    );

    return this.mapper.convertNotificationTicketPusatResolusiFromApi(res);
  }

  async getListTicket(
    params: ListTicketPusatResolusiRequest
  ) {
    const response = await this.service.invoke(
      "GET",
      this.endpoints.getTicketList(params)
    );

    return this.mapper.convertListTicketPusatResolusiFromApi(response);
  }

  async getSTTList(params: ListSTTPusatResolusi) {
    const response = await this.service.invoke(
      "GET",
      this.endpoints.getSTTList(params)
    );

    return this.mapper.convertSTTListFromApi(response);
  }

  async checkIssue(
    params: CheckTicketPusatResolusiRequest
  ): Promise<CheckIssuePusatResolusi> {
    const res = await this.service.invoke(
      "GET",
      this.endpoints.checkIssue(params)
    );

    return this.mapper.convertCheckIssuePusatResolusiFromApi(res);
  }

  async followTicket(id: number): Promise<ResponsePayload> {
    const res = await this.service.invoke(
      "POST",
      this.endpoints.followTicket(id)
    );

    return this.mapper.convertResponseFollowTicketFromApi(res);
  }

  async finishIssue(
    id: number,
    payload: FinishIssuePusatResolusiV2Request
  ): Promise<ResponsePayload> {
    const res = await this.service.invokePostWithFormData(
      "POST",
      this.endpoints.finishIssue(id),
      undefined,
      payload as FinishIssuePusatResolusiV2Request
    );

    return this.mapper.convertResponseFollowTicketFromApi(res);
  }
  async getTicketDetail(id: number): Promise<TicketDetail> {
    const res = await this.service.invoke(
      "GET",
      this.endpoints.getTicketDetail(id),
    );

    return this.mapper.convertTicketDetailFromApi(res);
  }

  async getTicketAction(id: number): Promise<TicketAction> {
    const res = await this.service.invoke(
      "GET",
      this.endpoints.getTicketAction(id)
    );

    return this.mapper.convertTicketActionFromApi(res);
  }

  async submitNote(payload: SubmitNotePusatResolusiRequest): Promise<boolean> {
    await this.service.invoke(
      "POST",
      this.endpoints.submitNote(payload.id),
      undefined,
      payload
    )

    return true;
  }

  async create(
    payload: CreatePusatResolusiV2RequestInterface
  ): Promise<TicketPusatResolusiDetail> {
    const resp = await this.service.invokePostWithFormData(
      "POST",
      this.endpoints.create(),
      {},
      payload as CheckTicketPusatResolusiRequest
    );
    return this.mapper.convertCreateTicketPusatResolusiFromApi(resp);
  }

  async sendChat(
    id: number,
    payload: SendChatPusatResolusiV2RequestInterface
  ): Promise<ResponsePayload> {
    const resp = await this.service.invokePostWithFormData(
      "POST",
      this.endpoints.sendChat(id),
      {},
      payload as SendChatPusatResolusiRequest
    );
    return this.mapper.convertResponseSendChatFromApi(resp);
  }

  async getMention(id: number): Promise<MentionsData[]> {
    const res = await this.service.invoke(
      "GET",
      this.endpoints.getMention(id)
    );

    return this.mapper.convertMentionDataFromApi(res);
  }

  async getConversation(
    id: number,
    params: GetConversationRequest
  ): Promise<HistoryChatResponse> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getConversation(id, params)
    );

    return this.mapper.convertHistoryChatResponseFromApi(resp);
  }

  connectConversationWebsocket(id: number): WebSocket {
    const socket = new WebSocket(`${Endpoints.baseUrl?.replaceAll("https", "wss")}/athena/ws/ticket/${id}?token=${getToken()}`);
    
    return socket;
  }
}
